import CheckCircleIcon from '@/comp/common/icons/CheckCircleIcon';
import CloseCircleIcon from '@/comp/common/icons/CloseCircleIcon';
import MinusCircleIcon from '@/comp/common/icons/MinusCircleIcon';
import ThreeDotsIcon from '@/comp/common/icons/ThreeDotsIcon';
import { TestParameter, TestState, ValidationDeviceStatus } from '@/store/InstallationValidation/constants';
import { t } from 'i18next';
import { SyncOutlined } from '@ant-design/icons';
import React from 'react';
import { GlobalToken } from 'antd';

export interface TestStatusCardProps {
  id: string;
  title: string;
  subTitle: string;
  connectivityIconAndStatus: { icon: JSX.Element; status: string };
  testState: TestState;
  LOAD?: TestParameter;
  UNLOAD?: TestParameter;
  isNoPowerSensorVisible?: boolean;
}

export const getStatusText = (
  status: ValidationDeviceStatus | undefined,
  testState: TestState,
  expectedValue?: number,
  realValue?: number,
) => {
  if (testState === TestState.INITIAL) {
    return t('INIT_SCENARIO');
  }
  if (status === ValidationDeviceStatus.NOT_TESTED && testState !== TestState.RUNNING) {
    return t('NOT_TESTED');
  }
  switch (status) {
    case ValidationDeviceStatus.PASS:
      return t('EXPECTED_VALUE_READING_VALUE', { expectedValue: expectedValue, readingValue: realValue });
    case ValidationDeviceStatus.FAILED:
      return t('EXPECTED_VALUE_READING_VALUE', { expectedValue: expectedValue, readingValue: realValue });
    case ValidationDeviceStatus.NOT_TESTED:
      return t('TESTING');
    case ValidationDeviceStatus.NOT_INCLUDE_SENSOR:
      return t('NOT_TESTED');
    default:
      return t('NOT_STARTED');
  }
};

export const getCommentButtonText = (testState: TestState, hasComments: boolean) => {
  if (testState === TestState.SUBMITTED) {
    return t('VIEW_COMMENT');
  }
  return hasComments ? t('VIEW_COMMENT') : t('ADD_COMMENT');
};

export const checkHasComments = (loadComment: string | undefined, unloadComment: string | undefined): boolean => {
  return Boolean(loadComment || unloadComment);
};

export const shouldShowCommentButton = (
  testState: TestState,
  loadStatus?: ValidationDeviceStatus,
  unloadStatus?: ValidationDeviceStatus,
): boolean => {
  return (
    (testState === TestState.NEEDS_REVIEW || testState === TestState.SUBMITTED) &&
    (loadStatus === ValidationDeviceStatus.FAILED ||
      loadStatus === ValidationDeviceStatus.NOT_TESTED ||
      unloadStatus === ValidationDeviceStatus.FAILED ||
      unloadStatus === ValidationDeviceStatus.NOT_TESTED)
  );
};

export const hasError = (
  testState: TestState,
  loadStatus?: ValidationDeviceStatus,
  unloadStatus?: ValidationDeviceStatus,
): boolean => {
  return (
    testState === TestState.NEEDS_REVIEW &&
    (loadStatus === ValidationDeviceStatus.FAILED ||
      loadStatus === ValidationDeviceStatus.NOT_TESTED ||
      unloadStatus === ValidationDeviceStatus.FAILED ||
      unloadStatus === ValidationDeviceStatus.NOT_TESTED)
  );
};

type StyleType = 'wrapper' | 'content' | 'color';

interface StyleConfig {
  wrapper?: string;
  content?: string;
  color?: (token: GlobalToken) => string;
}

const STYLE_MAP: Record<string, StyleConfig> = {
  // Running + Not Tested
  [`${TestState.RUNNING}_${ValidationDeviceStatus.NOT_TESTED}`]: {
    wrapper: 'testing-wrapper',
    color: token => token.colorPrimary,
  },

  // Initial OR Not Included Sensor
  [`${TestState.INITIAL}_*`]: {
    wrapper: 'not-tested-wrapper',
    content: 'status-content-common',
    color: token => token.colorIcon,
  },
  [`*_${ValidationDeviceStatus.NOT_INCLUDE_SENSOR}`]: {
    wrapper: 'not-tested-wrapper',
    content: 'status-content-common',
    color: token => token.colorIcon,
  },

  // Not Tested + Submitted / Needs Review
  [`${TestState.SUBMITTED}_${ValidationDeviceStatus.NOT_TESTED}`]: {
    wrapper: 'not-tested-wrapper',
    content: 'status-content-common',
    color: token => token.colorText,
  },
  [`${TestState.NEEDS_REVIEW}_${ValidationDeviceStatus.NOT_TESTED}`]: {
    wrapper: 'not-tested-wrapper',
    content: 'status-content-common',
    color: token => token.colorText,
  },

  [`*_${ValidationDeviceStatus.FAILED}`]: {
    wrapper: 'failed-test-wrapper',
    content: 'status-content-common',
  },

  [`*_${ValidationDeviceStatus.PASS}`]: {
    wrapper: 'pass-test-wrapper',
    content: 'status-content-common',
  },
};

export const getClassNameByTestStateAndStatus = (
  testState: TestState,
  status: ValidationDeviceStatus,
  type: StyleType,
  token: GlobalToken,
): string => {
  const keysToTry = [`${testState}_${status}`, `${testState}_*`, `*_${status}`];

  for (const key of keysToTry) {
    const config = STYLE_MAP[key];
    if (config) {
      const value = config[type];
      if (typeof value === 'function') {
        return value(token);
      }
      if (typeof value === 'string') {
        return value;
      }
    }
  }

  return '';
};

export const getStatusIcon = (testState: TestState, status?: ValidationDeviceStatus, color?: string) => {
  if (testState === TestState.INITIAL) {
    return <MinusCircleIcon size={17} />;
  }
  if (status === ValidationDeviceStatus.NOT_TESTED && testState !== TestState.RUNNING) {
    return <MinusCircleIcon color={color} size={17} />;
  }
  switch (status) {
    case ValidationDeviceStatus.PASS:
      return <CheckCircleIcon size={17} />;
    case ValidationDeviceStatus.FAILED:
      return <CloseCircleIcon size={17} />;
    //Testing...
    case ValidationDeviceStatus.NOT_TESTED:
      return <SyncOutlined color={color} size={17} />;
    case ValidationDeviceStatus.NOT_INCLUDE_SENSOR:
      return <MinusCircleIcon color={color} size={17} />;
    default:
      return <ThreeDotsIcon size={17} />;
  }
};
